import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import useGameStore from './gameStore';

const MAX_SLOTS = 3;
const LIVE_PERSIST_KEY = 'hst-game-save';

const useSaveStore = create(
  persist(
    (set, get) => ({
      saves: {},  // { slot1: { name, date, data }, slot2: ..., slot3: ... }
      // 17/07/2026 — slot « favori » mis en avant auprès du formateur (⭐).
      favoriteSlot: null,
      // 18/07/2026 — note pédagogique (1-2 phrases) jointe au slot favori,
      // visible par le formateur dans les vues supervision.
      favoriteNote: '',

      setFavoriteSlot: (slotKey) => {
        const isRemoving = get().favoriteSlot === slotKey;
        set({
          favoriteSlot: isRemoving ? null : slotKey,
          ...(isRemoving ? { favoriteNote: '' } : {}),
        });
      },

      setFavoriteNote: (note) => {
        set({ favoriteNote: (note || '').slice(0, 140) });
      },

      saveGame: (slotKey, gameState) => {
        const state = get();
        const saveData = {
          name: gameState.company?.name || 'Partie sans nom',
          date: new Date().toISOString(),
          totalDays: gameState.totalDays,
          treasury: gameState.treasury,
          phase: gameState.phase,
          vehicleCount: gameState.vehicles?.length || 0,
          driverCount: gameState.drivers?.length || 0,
          data: { ...gameState },
        };
        // Remove functions from data
        const cleaned = {};
        Object.keys(saveData.data).forEach(k => {
          if (typeof saveData.data[k] !== 'function') {
            cleaned[k] = saveData.data[k];
          }
        });
        saveData.data = cleaned;

        set({ saves: { ...state.saves, [slotKey]: saveData } });
        return true;
      },

      /**
       * Load a save into the live gameStore. Replaces all DATA keys but
       * preserves the slice action functions so the store remains usable
       * after the load.
       *
       * Algorithm :
       *   1. Snapshot the current store keys.
       *   2. Build a "cleared" object that sets every non-function key to
       *      `undefined` — this guarantees no key from the previous run can
       *      bleed through (bug recurrence #4, 27/05/2026).
       *   3. Merge the loaded snapshot on top — only data keys are overridden;
       *      action functions stay attached to the store.
       *
       * Returns { ok, reason? }.
       */
      loadGame: (slotKey) => {
        const save = get().saves[slotKey];
        if (!save || !save.data) {
          return { ok: false, reason: 'Slot vide.' };
        }
        try {
          const snapshot = JSON.parse(JSON.stringify(save.data));
          // 17/06/2026 — Migration en avant : les anciennes parties peuvent
          // contenir des chauffeurs (issus de rachat) avec `level: 'Junior'`
          // (string), ce qui casse le dashboard RH et la formation. On
          // normalise au chargement pour rattraper proprement.
          // eslint-disable-next-line global-require
          const { migrateDriversLevel } = require('../utils/driverLevel');
          if (snapshot && Array.isArray(snapshot.drivers)) {
            snapshot.drivers = migrateDriversLevel(snapshot.drivers);
          }
          const currentState = useGameStore.getState();
          const cleared = {};
          for (const k of Object.keys(currentState)) {
            if (typeof currentState[k] !== 'function') {
              cleared[k] = undefined;
            }
          }
          // Two-phase update: first clear all data keys (merge mode), then
          // apply the snapshot. We deliberately AVOID `setState(_, true)`
          // which would also wipe the slice actions.
          useGameStore.setState(cleared);
          useGameStore.setState(snapshot);
          return { ok: true };
        } catch (err) {
          return { ok: false, reason: `Erreur de chargement : ${err.message}` };
        }
      },

      deleteSave: (slotKey) => {
        const state = get();
        const newSaves = { ...state.saves };
        delete newSaves[slotKey];
        set({
          saves: newSaves,
          favoriteSlot: state.favoriteSlot === slotKey ? null : state.favoriteSlot,
          ...(state.favoriteSlot === slotKey ? { favoriteNote: '' } : {}),
        });
      },

      getSave: (slotKey) => {
        return get().saves[slotKey] || null;
      },

      getSlots: () => {
        const { saves, favoriteSlot } = get();
        return Array.from({ length: MAX_SLOTS }, (_, i) => {
          const key = `slot${i + 1}`;
          return { key, save: saves[key] || null, favorite: key === favoriteSlot };
        });
      },

      /**
       * Wipe the live game persistence and reload the page. Save slots are
       * preserved (they live under `hst-save-slots`, a separate key).
       *
       * The reload is the only bulletproof way to guarantee NO in-memory
       * variable from the previous run leaks into the new one — Zustand's
       * `set()` only updates keys you explicitly mention, so any state added
       * by a new slice but forgotten in `resetGame` would bleed without the
       * reload.
       */
      startNewGameFresh: () => {
        try {
          window.localStorage.removeItem(LIVE_PERSIST_KEY);
        } catch (err) { console.warn('[saveStore] live key removal failed (best-effort):', err); }
        if (typeof window !== 'undefined' && window.location && typeof window.location.reload === 'function') {
          window.location.reload();
        }
      },
    }),
    {
      name: 'hst-save-slots',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export default useSaveStore;
